import { useEffect, useRef } from "react";
import { useState } from "react";
import { useCallback } from "react";

import { LogSentryException, type ResponseAPI, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ConfirmationSection } from "../components/ConfirmationEduRequest/ConfirmationEduRequest";
import type { EduFormInputs } from "../components/EduForm/EduForm";
import EduForm from "../components/EduForm/EduForm";
import {
  InvalidEduRequest,
  InvalidEduRequestType,
} from "../components/InvalidEduRequest/InvalidEduRequest";
import LoadingPage from "../components/LoadingPage/LoadingPage";
import { GenericErrorModal } from "../components/Modal/Modal";
import NonEduEmailWarning from "../components/NonEduEmailWarning/NonEduEmailWarning";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../components/ReCAPTCHA/ReCAPTCHA";
import { recaptchaTokenHeaderName } from "../config";
import useNavigation from "../hooks/navigation";
import { accountQueryKey } from "../hooks/useAccount";
import { TeamSubscriptionsQueryKey } from "../hooks/useTeams";
import { UMSrequestClient } from "../stores/requestClient";
import { ServiceErrorCode } from "../types";

export enum EduRequestState {
  NotSent,
  Approved = "approved",
  Requested = "requested",
  AlreadyEdu = "already-edu",
  AlreadyRequestedEdu = "already-requested",
  VerificationNeeded = "verification-needed",
  Error = "error",
}

export type CheckEduAddressResult = {
  email: string;
  eduRequestStatus?: EduRequestStatus;
  hasEduDomain: boolean;
  verified: boolean;
  dueToRenew?: boolean;
  eduEmailAlreadyUsed?: boolean;
  eduRequestState?: EduRequestState;
};

export enum EduRequestStatus {
  Pending = "pending",
  Declined = "declined",
  Approved = "approved",
}
const fileToBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split("base64,")[1]);
    reader.onerror = (error) => reject(error);
  });

const EduRequestContainer = () => {
  const { goToEduConfirmation, goToProfile } = useNavigation();
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const [isRecaptchaError, setIsRecaptchaError] = useState(false);

  const queryClient = useQueryClient();

  const checkIfEduAddress = async ({ signal }: { signal?: AbortSignal }) => {
    try {
      const captcha = await recaptchaRef.current?.execute();
      return UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: "/edu/edu-request",
        extraHeaders: { [recaptchaTokenHeaderName]: captcha ?? "" },
        abortSignal: signal,
      }).then((res: ResponseAPI) => {
        return res.json<CheckEduAddressResult>();
      });
    } catch (error) {
      LogSentryException({ messagePrefix: "Captcha error", error });
    }
  };

  const {
    data: checkEduAddressResult,
    isLoading,
    error: eduVerificationError,
  } = useQuery({
    queryKey: ["check-edu-address"],
    queryFn: checkIfEduAddress,
    staleTime: Infinity,
    enabled: isRecaptchaLoaded,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select: (data) => {
      if (!data?.verified && data) {
        data.eduRequestState = EduRequestState.VerificationNeeded;
      } else if (data?.eduRequestStatus === EduRequestStatus.Pending) {
        data.eduRequestState = EduRequestState.AlreadyRequestedEdu;
      } else if (data?.eduRequestStatus === EduRequestStatus.Approved && !data?.dueToRenew) {
        data.eduRequestState = EduRequestState.AlreadyEdu;
      }
      return data;
    },
  });

  const submit = useCallback(async (data: EduFormInputs) => {
    const encodedFiles = await Promise.all(data.idPhotos?.map((file) => fileToBase64(file)) ?? []);
    try {
      const captcha = await recaptchaRef.current?.execute();
      return UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: "/edu-requests",
        extraHeaders: { [recaptchaTokenHeaderName]: captcha ?? "" },
        body: {
          ...data,
          idPhotos: encodedFiles,
        },
      });
    } catch (error) {
      LogSentryException({ messagePrefix: "Captcha error", error });
    }
  }, []);

  const {
    isSuccess: isSuccessEduRequest,
    isPending: submittingEduRequest,
    isError,
    mutate: eduFormSubmit,
  } = useMutation({
    mutationFn: submit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueryKey });
      queryClient.invalidateQueries({ queryKey: TeamSubscriptionsQueryKey });
    },
  });

  useEffect(() => {
    if (isSuccessEduRequest) {
      if (checkEduAddressResult?.hasEduDomain) {
        if (checkEduAddressResult?.verified) {
          goToEduConfirmation(EduRequestState.Approved);
        } else {
          goToEduConfirmation(EduRequestState.VerificationNeeded);
        }
      } else {
        goToEduConfirmation(EduRequestState.Requested);
      }
    }
  }, [isSuccessEduRequest, checkEduAddressResult, goToEduConfirmation]);

  const [nonEduWarningDismissed, setNonEduWarningDismissed] = useState(false);

  if (
    checkEduAddressResult?.eduRequestState !== undefined &&
    checkEduAddressResult?.eduRequestState !== EduRequestState.VerificationNeeded
  ) {
    goToEduConfirmation(
      checkEduAddressResult?.eduRequestState === undefined
        ? (EduRequestState.NotSent as string)
        : (checkEduAddressResult.eduRequestState as string)
    );
    return null;
  }

  if (!isRecaptchaError && (isLoading || !isRecaptchaLoaded)) {
    return (
      <>
        <LoadingPage />
        <ReCAPTCHA
          ref={recaptchaRef}
          onLoad={() => {
            setIsRecaptchaLoaded(true);
          }}
          onError={() => setIsRecaptchaError(true)}
        />
      </>
    );
  }

  if (checkEduAddressResult?.eduEmailAlreadyUsed) {
    return (
      <ConfirmationSection title="This email was already used to claim an educational license" />
    );
  }

  if (
    eduVerificationError instanceof ServiceError &&
    eduVerificationError.hasCode(ServiceErrorCode.EduRequestUnavailable)
  ) {
    return <InvalidEduRequest type={InvalidEduRequestType.Unavailable} />;
  }

  if (
    eduVerificationError instanceof ServiceError &&
    eduVerificationError.hasCode(ServiceErrorCode.EduRequestIneligible)
  ) {
    return <InvalidEduRequest type={InvalidEduRequestType.IneligibleEmail} />;
  }

  if (eduVerificationError) {
    return <GenericErrorModal open={true} onClose={goToProfile} telemetryName="edu_request" />;
  }

  if (checkEduAddressResult?.eduRequestState === EduRequestState.VerificationNeeded) {
    return <InvalidEduRequest type={InvalidEduRequestType.EmailVerificationRequired} />;
  }

  if (!checkEduAddressResult?.hasEduDomain && !nonEduWarningDismissed) {
    return <NonEduEmailWarning dismissWarning={() => setNonEduWarningDismissed(true)} />;
  }

  return (
    <>
      <EduForm
        submit={eduFormSubmit}
        isSubmitting={submittingEduRequest}
        showStudentIDUpload={!checkEduAddressResult?.hasEduDomain}
        errorMessage={isError ? "Something went wrong. Please try again." : ""}
      />
      <ReCAPTCHA
        ref={recaptchaRef}
        onLoad={() => {
          setIsRecaptchaLoaded(true);
        }}
        onError={() => setIsRecaptchaError(true)}
      />
    </>
  );
};

export default EduRequestContainer;
